<template>
  <div class="TextField">
    <div class="TextField__inner">
      <input
        v-if="isPassword"
        :id="id"
        class="TextField__form"
        :class="{
          [`TextField__form--error`]: isError,
          [`TextField__form--password`]: isPassword,
        }"
        :type="inputType"
        :name="name"
        :aria-label="ariaLabel"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :value="value"
        @input="$emit('input', ($event.target as HTMLInputElement).value)"
      />
      <input
        v-else-if="inputTypeSelect"
        :id="id"
        class="TextField__form"
        :class="{ [`TextField__form--error`]: isError }"
        :type="inputTypeSelect"
        :name="name"
        :aria-label="ariaLabel"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :value="value"
        @input="$emit('input', ($event.target as HTMLInputElement).value)"
      />
      <input
        v-else
        :id="id"
        class="TextField__form"
        :class="{
          [`TextField__form--error`]: isError,
        }"
        :type="type"
        :name="name"
        :value="value"
        :aria-label="ariaLabel"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :min="min"
        :max="max"
        @input="onInput"
        @blur="onBlur"
      />
      <Icon
        v-if="isPassword"
        class="TextField__icon"
        :icon-name="passwordIcon ? 'PasswordOn' : 'PasswordOff'"
        @click="onClick"
      />
    </div>
    <span v-if="isError && errorText !== ''" class="TextField__errorText">{{
      errorText
    }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Icon from '@/components/atoms/IconBase.vue';

defineOptions({
  name: 'TextField',
});

interface Props {
  type?: string;
  name?: string;
  id?: string;
  value?: string;
  ariaLabel: string;
  placeholder?: string;
  isDisabled?: boolean;
  isError?: boolean;
  isPassword?: boolean;
  errorText?: string;
  min?: number;
  max?: number;
  inputTypeSelect?: string | null;
}

withDefaults(defineProps<Props>(), {
  type: '',
  name: '',
  id: '',
  value: '',
  placeholder: '',
  isDisabled: false,
  isError: false,
  isPassword: false,
  errorText: '',
  min: 0,
  max: 0,
  inputTypeSelect: null,
});

const emit = defineEmits<{
  (e: 'input', value: string): void;
  (e: 'blur', event: FocusEvent): void;
}>();

const passwordIcon = ref(false);

const inputType = computed(() => {
  return passwordIcon.value ? 'text' : 'password';
});

function onClick(): void {
  passwordIcon.value = !passwordIcon.value;
}

function onInput(e: Event): void {
  emit('input', (e.target as HTMLInputElement).value);
}

function onBlur(e: FocusEvent): void {
  emit('blur', e);
}
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_forward.scss' as *;

.TextField {
  &__form {
    display: flex;
    width: 100%;
    height: $service-unit * 10;
    padding: 0 $service-unit * 4;
    font-size: $service-font-size-p;
    color: $color-gray-80;
    background-color: $color-white-100;
    border: 1px solid $color-gray-50;
    border-radius: $service-border-radius;
    caret-color: $color-blue-60;

    &::placeholder {
      color: $color-gray-50;
    }

    &:hover {
      border: 1px solid $color-blue-60;
    }

    &:focus {
      outline: 2px solid $color-blue-60;
    }

    &:disabled {
      background-color: $color-gray-30;
      border: 0;
    }

    &--error {
      border: 2px solid $color-red-70;

      &:hover {
        border: 2px solid $color-red-70;
      }
    }

    &--password {
      padding: 0 $service-unit * 11 0 $service-unit * 4;
    }
  }

  &__inner {
    position: relative;
  }

  & &__icon {
    position: absolute;
    top: 50%;
    right: $service-unit * 4;
    width: $service-unit * 6;
    height: $service-unit * 6;
    color: $color-gray-50;
    cursor: pointer;
    transform: translateY(-50%);
  }

  &__errorText {
    display: block;
    margin-top: $service-unit;
    font-size: $service-font-size-pp;
    color: $color-red-70;
    text-align: left;
  }
}
</style>
